<template>
  <div class="demo-page">
    <div class="demo-header">
      <div class="demo-header-inner">
        <img src="@/assets/images/chatnav_logo.svg" alt="ChatNav" class="demo-logo-img">
        <span class="demo-powered">Powered by ChatNav</span>
      </div>
      <span class="demo-preview-badge">Demo Preview</span>
    </div>

    <div class="demo-main">
      <div class="demo-hero">
        <div class="demo-hero-tag">🤖 AI Assistant</div>
        <h1>Meet <span class="demo-agent-name">{{ agentName }}</span></h1>
        <p class="demo-hero-sub">
          Your AI assistant is live and ready to help. Ask it anything — try it right now.
        </p>
        <p class="demo-limit-note">
          The number of messages in a demo conversation is limited. If the chat says the limit was reached,
          click the <strong>↻ New conversation</strong> icon at the top of the chat window to start again.
        </p>
        <button class="demo-start-btn" @click="openChat">
          <span class="demo-start-icon">💬</span>
          Start chatting
        </button>
        <transition name="hint-fade">
          <div v-if="showHint" class="demo-hint">
            <strong>👉 Look for the chat icon in the bottom-right corner</strong>
            <span> — click it, type your question, and hit send!</span>
            <span class="demo-hint-arrow"> ↘</span>
          </div>
        </transition>
      </div>

      <div class="demo-features">
        <div class="demo-feature">
          <div class="demo-feature-icon">⚡</div>
          <h3>Instant answers</h3>
          <p>No waiting, no hold music. Ask a question and get a response immediately.</p>
        </div>
        <div class="demo-feature">
          <div class="demo-feature-icon">🕐</div>
          <h3>Available 24/7</h3>
          <p>Day or night, weekdays or holidays — the assistant is always on and ready.</p>
        </div>
        <div class="demo-feature">
          <div class="demo-feature-icon">🎯</div>
          <h3>Trained for this business</h3>
          <p>Knows your products, services, and how to help your specific customers.</p>
        </div>
      </div>

      <div class="demo-try-section">
        <h2>How to try it</h2>
        <div class="demo-steps">
          <div class="demo-step">
            <div class="demo-step-num">1</div>
            <p>Find the <strong>chat icon</strong> in the bottom-right corner of this page and click it to open the chat.</p>
          </div>
          <div class="demo-step">
            <div class="demo-step-num">2</div>
            <p>Type a question — anything you'd normally ask a customer support agent.</p>
          </div>
          <div class="demo-step">
            <div class="demo-step-num">3</div>
            <p>Hit send and see your AI assistant respond in real time.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentDemo',
  data() {
    return {
      showHint: false,
      chatReady: false,
    }
  },
  computed: {
    subdomain() {
      return this.$route.params.subdomain;
    },
    agentName() {
      return sessionStorage.getItem('chatnav_demo_name') || this.$route.params.subdomain;
    }
  },
  mounted() {
    const subdomain = this.$route.params.subdomain;
    const script = document.createElement('script');
    script.src = `https://${subdomain}.chatnav.net/static/chatbot/embed.js`;
    script.id = 'chatnav-bot';
    script.async = true;
    script.onload = () => { this.chatReady = true; };
    document.body.appendChild(script);
  },
  beforeUnmount() {
    const script = document.getElementById('chatnav-bot');
    if (script) script.remove();
    const iframe = document.getElementById('chat-iframe');
    if (iframe) iframe.remove();
  },
  methods: {
    openChat() {
      this.showHint = true;
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    },
  },
}
</script>

<style scoped>
.demo-page {
  min-height: 100vh;
  background: #f5f3ff;
  font-family: 'Inter', sans-serif;
}

/* ── Header ── */
.demo-header {
  background: #fff;
  border-bottom: 1px solid #e7e7e7;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.demo-header-inner {
  display: flex;
  align-items: center;
  gap: 10px;
}
.demo-logo-img {
  height: 28px;
}
.demo-powered {
  font-size: 13px;
  color: #888;
  font-weight: 500;
}
.demo-limit-note {
  font-size: 14px;
  color: #777;
  line-height: 1.5;
  max-width: 520px;
  margin: 0;
}
.demo-preview-badge {
  background: #f3f0ff;
  color: #6f2bff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #e0d8ff;
}

/* ── Main layout ── */
.demo-main {
  max-width: 860px;
  margin: 0 auto;
  padding: 56px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 56px;
}

/* ── Hero ── */
.demo-hero {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}
.demo-hero-tag {
  background: #fff;
  border: 1px solid #e0d8ff;
  color: #6f2bff;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
  display: inline-block;
}
.demo-hero h1 {
  font-size: 42px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.15;
}
.demo-agent-name {
  color: #6f2bff;
}
.demo-hero-sub {
  font-size: 18px;
  color: #555;
  line-height: 1.6;
  max-width: 520px;
  margin: 0;
}
.demo-start-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #6f2bff;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 16px 36px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 4px 20px rgba(111,43,255,0.35);
  margin-top: 4px;
}
.demo-start-btn:hover {
  background: #5a1fe0;
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(111,43,255,0.45);
}
.demo-start-btn:active {
  transform: translateY(0);
}
.demo-start-icon {
  font-size: 20px;
}
.demo-hint {
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  padding: 12px 20px;
  font-size: 14px;
  color: #555;
  box-shadow: 0 2px 8px rgba(111,43,255,0.1);
}
.demo-hint-arrow {
  font-size: 18px;
  color: #6f2bff;
  font-weight: 700;
}
.hint-fade-enter-active, .hint-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.hint-fade-enter-from, .hint-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Feature cards ── */
.demo-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
.demo-feature {
  background: #fff;
  border-radius: 14px;
  padding: 28px 24px;
  border: 1px solid #e7e7e7;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.demo-feature-icon {
  font-size: 28px;
  margin-bottom: 12px;
}
.demo-feature h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}
.demo-feature p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* ── How to try it ── */
.demo-try-section {
  background: #fff;
  border-radius: 16px;
  padding: 36px 40px;
  border: 1px solid #e7e7e7;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.demo-try-section h2 {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 28px 0;
}
.demo-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.demo-step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.demo-step-num {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: #6f2bff;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  margin-top: 1px;
}
.demo-step p {
  font-size: 15px;
  color: #444;
  line-height: 1.6;
  margin: 0;
  padding-top: 4px;
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .demo-main {
    padding: 36px 20px 60px;
    gap: 36px;
  }
  .demo-hero h1 {
    font-size: 28px;
  }
  .demo-hero-sub {
    font-size: 16px;
  }
  .demo-start-btn {
    padding: 14px 28px;
    font-size: 16px;
    width: 100%;
    justify-content: center;
  }
  .demo-features {
    grid-template-columns: 1fr;
  }
  .demo-try-section {
    padding: 24px 20px;
  }
  .demo-header {
    padding: 12px 20px;
  }
}
</style>
